import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiUser, 
  FiUsers, 
  FiTarget, 
  FiBriefcase, 
  FiLogOut, 
  FiSettings, 
  FiMenu, 
  FiX,
  FiBookOpen,
} from 'react-icons/fi';
import {
  getUserType,
  getUserTypeLabel,
  getQuickActionsForUserType,
  getHomePathForUser,
} from '../utils/userTypeUtils';
import {
  PUBLIC_NAV_LINKS,
  PUBLIC_JOIN_LINKS,
  PUBLIC_FOOTER_LINKS,
  isPublicJoinActive,
} from '../constants/publicNav';
import SiteGuide from './SiteGuide';

const Header = styled.header`
  background: white;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  padding: 0.85rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 1200;
  min-height: 72px;
  gap: 1rem;
  overflow: visible;
  
  @media (max-width: 1536px) {
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  flex-shrink: 1;
`;

const Logo = styled(Link)`
  font-size: 1.45rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const LogoIcon = styled.span`
  width: 36px;
  height: 36px;
  background: ${props => props.theme.colors.primary};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const Tagline = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 400;
  margin-top: 0.18rem;
  margin-left: 0.1rem;
  letter-spacing: 0.04em;
  line-height: 1.1;
  max-width: 14rem;
  overflow-wrap: break-word;
  
  @media (max-width: 768px) {
    margin-left: 0;
    text-align: left;
    max-width: 11rem;
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
  justify-content: flex-end;
  
  @media (max-width: 1536px) {
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
    flex: 0 0 auto;
  }
`;

const LeftNav = styled.nav`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  min-width: 0;
  
  @media (max-width: 1536px) {
    display: none;
  }
`;

const RightNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;
  
  @media (max-width: 1536px) {
    display: none;
  }
`;

const ExternalNavLink = styled.a`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.background};
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.45rem 0.65rem;
  border-radius: 6px;
  background: ${props => props.active ? props.theme.colors.background : 'transparent'};
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.background};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.4rem 0.6rem;
    white-space: normal;
    overflow-wrap: break-word;
  }
`;

const RegistrationButton = styled(Link)`
  text-decoration: none;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.4rem 0.7rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: normal;
  text-align: center;
  justify-content: center;
  line-height: 1.2;
  max-width: 100%;
  overflow-wrap: break-word;
  
  svg {
    flex-shrink: 0;
  }
  
  &.candidate {
    background: #0F766E;
    color: white;
    border: 1px solid #0F766E;
    
    &:hover {
      background: #115E59;
    }
  }
  
  &.consultancy {
    background: #0F766E;
    color: white;
    border: 1px solid #0F766E;
    
    &:hover {
      background: #115E59;
    }
  }

  &.institution {
    background: #F8FAFC;
    color: #0F766E;
    border: 2px solid #0F766E;
    
    &:hover {
      background: #E2E8F0;
    }
  }
  
  @media (max-width: 1280px) {
    font-size: 0.72rem;
    padding: 0.35rem 0.55rem;
    white-space: normal;
    text-align: center;
    justify-content: center;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
    white-space: normal;
    text-align: center;
    justify-content: center;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: 0.5rem;
  }
`;

const Username = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  text-align: center;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  margin-top: 0.5rem;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  @media (max-width: 768px) {
    position: static;
    margin-top: 0.5rem;
    min-width: 100%;
    box-shadow: none;
    border: 1px solid #e5e7eb;
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #374151;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    color: ${props => props.theme.colors.primary};
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
    border-top: 1px solid #e5e7eb;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #dc2626;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #fef2f2;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
`;

const UserTypeBadge = styled.span`
  background: ${props => props.userType === 'candidate' ? '#0D9488' : props.userType === 'consultancy' ? '#EA580C' : '#0284C7'};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }
`;

const Footer = styled.footer`
  background: #f8f8f8;
  color: #888;
  text-align: center;
  padding: 1.5rem 1rem 5.5rem;
  font-size: 0.95rem;
  margin-top: auto;
  overflow-wrap: break-word;
  min-width: 0;

  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem 5rem;
    font-size: 0.85rem;
  }
`;

const FooterJoin = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem 0.85rem;
  margin-bottom: 1rem;
`;

const FooterJoinLink = styled(Link)`
  color: #0F766E;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: break-word;
`;

const FooterMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.25rem 0.15rem;
  padding: 0 0.5rem;
`;

const Main = styled.main`
  min-height: 70vh;
  padding: 2rem 0 2rem 0;
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1.25rem 0;
  }
`;
const joinIcon = (type, size = 16) => {
  if (type === 'consultancy') return <FiUsers size={size} />;
  if (type === 'institution') return <FiBriefcase size={size} />;
  if (type === 'partner') return <FiBookOpen size={size} />;
  return <FiUser size={size} />;
};

const CompactJoin = styled.div`
  display: none;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  min-width: 0;
  justify-content: flex-end;
  max-width: min(100%, 42rem);

  @media (max-width: 1536px) and (min-width: 768px) {
    display: flex;
    flex: 1 1 auto;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 1.25rem;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  padding: 0.4rem 0.55rem;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 1536px) {
    display: flex;
  }
`;

const MobileNav = styled.div`
  display: none;
  
  @media (max-width: 1536px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-basis: 100%;
    flex-direction: column;
    gap: 0.35rem;
    width: 100%;
    padding: 0.75rem 0 0.5rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 0.75rem;
    /* Sticky header can exceed the viewport; keep the panel scrollable on phones. */
    max-height: min(75vh, calc(100vh - 5.5rem));
    max-height: min(75dvh, calc(100dvh - 5.5rem));
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
`;

const MobileExternalNavLink = styled.a`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.background};
  }
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: ${props => props.active ? props.theme.colors.background : 'transparent'};
  transition: all 0.2s;
  text-align: center;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.background};
  }
`;

const MobileRegistrationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  margin: 0.5rem 0;
`;

const MobileRegistrationButton = styled(Link)`
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  
  &.candidate {
    background: #0F766E;
    color: white;
    border: 1px solid #0F766E;
  }
  
  &.consultancy {
    background: #0F766E;
    color: white;
    border: 1px solid #0F766E;
  }

  &.institution {
    background: #F8FAFC;
    color: #0F766E;
    border: 2px solid #0F766E;
  }
`;

const CommonLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const quickActions = user ? getQuickActionsForUserType(user).filter((a) => !a.comingSoon) : [];
  const homePath = getHomePathForUser(user);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <Header>
        <LogoContainer>
          <Logo to="/">
            <LogoIcon>EW</LogoIcon>
            EnterWeU
          </Logo>
          <Tagline>Seven interview modes · talent discovery</Tagline>
        </LogoContainer>
        
        <NavContainer>
          <LeftNav>
          {PUBLIC_NAV_LINKS.map(link => (
            link.external ? (
              <ExternalNavLink key={link.label} href={typeof link.to === 'function' ? link.to() : link.to}>
                {link.label}
              </ExternalNavLink>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                active={location.pathname === link.to}
              >
                {link.label}
              </NavLink>
            )
          ))}
          </LeftNav>
          
          <RightNav>
          {user ? (
              <UserMenu className="user-menu">
                <UserInfo>
                  <UserAvatar onClick={toggleUserMenu}>
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </UserAvatar>
                  <Username>{user.username || 'User'}</Username>
                  <UserTypeBadge userType={getUserType(user)}>
                    {getUserTypeLabel(user)}
                  </UserTypeBadge>
                </UserInfo>
                
                <UserDropdown isOpen={userMenuOpen}>
                  <DropdownItem to={homePath}>
                    <FiTarget size={16} />
              Dashboard
                  </DropdownItem>
                  
                  {/* Quick Actions Section */}
                  <div style={{ 
                    padding: '0.5rem 1rem', 
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Quick Actions
                  </div>
                  
                  {quickActions.map((action) => (
                    <DropdownItem key={action.slug} to={action.path}>
                      {action.slug.includes('candidate') || action.slug.includes('talent') || action.slug.includes('bulk') || action.slug.includes('user')
                        ? <FiUsers size={16} />
                        : action.slug.includes('job') || action.slug.includes('client') || action.slug.includes('placement')
                          ? <FiBriefcase size={16} />
                          : <FiTarget size={16} />}
                      {action.title}
                    </DropdownItem>
                  ))}
                  
                  {/* Account Section */}
                  <div style={{ 
                    padding: '0.5rem 1rem', 
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Account
                  </div>
                  
                  <DropdownItem to="/profile">
                    <FiUser size={16} />
                    Profile
                  </DropdownItem>
                  
                  <DropdownItem to="/settings">
                    <FiSettings size={16} />
                    Settings
                  </DropdownItem>
                  
                  <LogoutButton onClick={handleLogout}>
                    <FiLogOut size={16} />
                    Logout
                  </LogoutButton>
                </UserDropdown>
              </UserMenu>
            ) : (
              <>
            <NavLink to="/login" active={location.pathname === '/login'}>
              Login
            </NavLink>
                {PUBLIC_JOIN_LINKS.map((link) => (
                  <RegistrationButton
                    key={link.to}
                    to={link.to}
                    className={link.type}
                    aria-current={isPublicJoinActive(location.pathname, link.to) ? 'page' : undefined}
                  >
                    {joinIcon(link.type)}
                    {link.label}
                  </RegistrationButton>
                ))}
              </>
            )}
          </RightNav>

          {!user && (
            <CompactJoin>
              {PUBLIC_JOIN_LINKS.map((link) => (
                <RegistrationButton
                  key={`compact-${link.to}`}
                  to={link.to}
                  className={link.type}
                  aria-current={isPublicJoinActive(location.pathname, link.to) ? 'page' : undefined}
                >
                  {joinIcon(link.type, 14)}
                  {link.label}
                </RegistrationButton>
              ))}
            </CompactJoin>
          )}
          
          <MobileMenuToggle onClick={toggleMobileMenu} aria-label="Open menu">
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </MobileMenuToggle>
        </NavContainer>
        
        <MobileNav isOpen={mobileMenuOpen}>
          {!user && (
            <MobileRegistrationSection>
              {PUBLIC_JOIN_LINKS.map((link) => (
                <MobileRegistrationButton
                  key={link.to}
                  to={link.to}
                  className={link.type}
                  aria-current={isPublicJoinActive(location.pathname, link.to) ? 'page' : undefined}
                >
                  {joinIcon(link.type)}
                  {link.label}
                </MobileRegistrationButton>
              ))}
            </MobileRegistrationSection>
          )}
          {PUBLIC_NAV_LINKS.map(link => (
            link.external ? (
              <MobileExternalNavLink key={link.label} href={typeof link.to === 'function' ? link.to() : link.to}>
                {link.label}
              </MobileExternalNavLink>
            ) : (
              <MobileNavLink
                key={link.to}
                to={link.to}
                active={location.pathname === link.to}
              >
                {link.label}
              </MobileNavLink>
            )
          ))}
          
          {user ? (
            <>
              <MobileNavLink to={homePath} active={location.pathname === homePath}>
                Dashboard
              </MobileNavLink>
              
              {quickActions.map((action) => (
                <MobileNavLink key={action.slug} to={action.path}>
                  {action.title}
                </MobileNavLink>
              ))}
              
              <MobileNavLink to="/profile">
                Profile
              </MobileNavLink>
              
              <MobileNavLink to="/settings">
                Settings
              </MobileNavLink>
              
              <LogoutButton onClick={handleLogout} style={{ margin: '0 1rem' }}>
                <FiLogOut size={16} />
                Logout
              </LogoutButton>
            </>
          ) : (
            <>
              <MobileNavLink to="/login" active={location.pathname === '/login'}>
                Login
              </MobileNavLink>
            </>
          )}
        </MobileNav>
      </Header>
      
      <Main>{children}</Main>
      
      <Footer>
        {!user && (
          <FooterJoin>
            <strong>Join EnterWeU:</strong>
            {PUBLIC_JOIN_LINKS.map((link) => (
              <FooterJoinLink key={link.to} to={link.to}>
                {joinIcon(link.type, 14)}
                {link.label}
              </FooterJoinLink>
            ))}
          </FooterJoin>
        )}
        <FooterMeta>
          <span>&copy; {new Date().getFullYear()} EnterWeU. All rights reserved.</span>
          {PUBLIC_FOOTER_LINKS.map((link) => (
            <React.Fragment key={link.label}>
              <span style={{ margin: '0 0.15rem', color: '#CBD5E1' }}>|</span>
              {link.external ? (
                <a href={typeof link.to === 'function' ? link.to() : link.to} style={{ color: '#64748B', margin: '0 0.4rem' }}>{link.label}</a>
              ) : (
                <Link to={link.to} style={{ color: '#64748B', margin: '0 0.4rem' }}>{link.label}</Link>
              )}
            </React.Fragment>
          ))}
        </FooterMeta>
      </Footer>

      <SiteGuide user={user} />
    </>
  );
};

export default CommonLayout; 